import { useEffect, useRef, useState } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from '../components/Helmet';
import { motion } from 'framer-motion';
import { Calendar, User, Clock, ArrowLeft, Facebook, Linkedin, Twitter, ListTree, Sparkles, ChevronDown, GraduationCap, ChevronRight } from 'lucide-react';
import { getBlogPostBySlug, getRelatedPosts } from '../utils/blogUtils';
import { getRecommendedCourses } from '../data/courses';
import { MarkdownRenderer } from '../components/blog/MarkdownRenderer';
import { RelatedArticles } from '../components/blog/RelatedArticles';
import { ArticleSchema } from '../components/blog/ArticleSchema';

interface TocItem {
    id: string;
    text: string;
    level: number;
}

const BlogArticle = () => {
    const { slug } = useParams<{ slug: string }>();
    const contentRef = useRef<HTMLDivElement>(null);
    const [toc, setToc] = useState<TocItem[]>([]);
    const [activeId, setActiveId] = useState<string>('');
    const [mobileTocOpen, setMobileTocOpen] = useState(false);

    if (!slug) {
        return <Navigate to="/blog/" replace />;
    }

    const article = getBlogPostBySlug(slug);

    if (!article) {
        return <Navigate to="/blog/" replace />;
    }

    const relatedArticles = getRelatedPosts(article, 3);
    const recommendedCourses = getRecommendedCourses(
        article.slug,
        [...(article.tags || []), article.title],
        2,
        article.relatedTraining || []
    );

    const shareUrl = article.seo.canonicalUrl;
    const shareTitle = article.title;

    // Optional editorial fields — add these to the BlogPost type/data when ready.
    // Falls back gracefully so this renders even before the fields exist.
    const authorBio = (article as any).authorBio as string | undefined;
    const authorAvatar = (article as any).authorAvatar as string | undefined;
    const faq = (article as any).faq as { question: string; answer: string }[] | undefined;

    useEffect(() => {
        if (!contentRef.current) return;
        const headings = Array.from(contentRef.current.querySelectorAll('h2, h3')) as HTMLElement[];
        const items: TocItem[] = headings.map((h, i) => {
            if (!h.id) h.id = `section-${i}`;
            h.style.scrollMarginTop = '96px';
            return { id: h.id, text: h.textContent || '', level: h.tagName === 'H2' ? 2 : 3 };
        });
        setToc(items);
        setActiveId(items[0]?.id || '');
    }, [article.content]);

    const scrollToHeading = (id: string) => {
        const el = document.getElementById(id);
        if (!el) return;

        setActiveId(id);
        el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        setMobileTocOpen(false);
    };

    const handleShare = (platform: 'facebook' | 'twitter' | 'linkedin') => {
        let url = '';
        switch (platform) {
            case 'facebook':
                url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`;
                break;
            case 'twitter':
                url = `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(shareTitle)}`;
                break;
            case 'linkedin':
                url = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`;
                break;
        }
        window.open(url, '_blank', 'width=600,height=400');
    };

    const shareButtons = (size: 'sm' | 'lg' = 'sm') => (
        <div className="flex gap-2">
            <button
                onClick={() => handleShare('linkedin')}
                className={`${size === 'lg' ? 'p-3' : 'p-2'} rounded-full bg-primary/5 hover:bg-primary/10 text-primary transition-colors`}
                aria-label="Partager sur LinkedIn"
            >
                <Linkedin size={size === 'lg' ? 20 : 18} />
            </button>
            <button
                onClick={() => handleShare('twitter')}
                className={`${size === 'lg' ? 'p-3' : 'p-2'} rounded-full bg-primary/5 hover:bg-primary/10 text-primary transition-colors`}
                aria-label="Partager sur Twitter"
            >
                <Twitter size={size === 'lg' ? 20 : 18} />
            </button>
            <button
                onClick={() => handleShare('facebook')}
                className={`${size === 'lg' ? 'p-3' : 'p-2'} rounded-full bg-primary/5 hover:bg-primary/10 text-primary transition-colors`}
                aria-label="Partager sur Facebook"
            >
                <Facebook size={size === 'lg' ? 20 : 18} />
            </button>
        </div>
    );

    const renderTocList = () => (
        <ul className="space-y-1">
            {toc.map((item) => (
                <li key={item.id}>
                    <button
                        onClick={() => scrollToHeading(item.id)}
                        className={`w-full text-left text-sm py-1.5 leading-snug transition-colors border-l-2 ${
                            item.level === 3 ? 'pl-6' : 'pl-3'
                        } ${
                            activeId === item.id
                                ? 'border-primary text-primary font-medium'
                                : 'border-transparent text-gray-500 hover:text-gray-900 hover:border-gray-300'
                        }`}
                    >
                        {item.text}
                    </button>
                </li>
            ))}
        </ul>
    );

    return (
        <>
            <Helmet>
                <title>{article.title}</title>
                <meta name="description" content={article.seo.metaDescription} />
                <meta name="keywords" content={article.seo.keywords.join(', ')} />

                {/* Open Graph */}
                <meta property="og:title" content={article.title} />
                <meta property="og:description" content={article.excerpt} />
                <meta property="og:type" content="article" />
                <meta property="og:url" content={article.seo.canonicalUrl} />
                <meta property="og:image" content={article.seo.ogImage || article.image} />
                <meta property="og:image:width" content="1200" />
                <meta property="og:image:height" content="630" />

                {/* Twitter Card */}
                <meta name="twitter:card" content="summary_large_image" />
                <meta name="twitter:title" content={article.title} />
                <meta name="twitter:description" content={article.excerpt} />
                <meta name="twitter:image" content={article.seo.ogImage || article.image} />

                <link rel="canonical" href={article.seo.canonicalUrl} />

                {/* BreadcrumbList schema */}
                <script type="application/ld+json">
                    {JSON.stringify({
                        '@context': 'https://schema.org',
                        '@type': 'BreadcrumbList',
                        itemListElement: [
                            { '@type': 'ListItem', position: 1, name: 'Accueil', item: 'https://www.upnov.com' },
                            { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://www.upnov.com/blog/' },
                            { '@type': 'ListItem', position: 3, name: article.title, item: article.seo.canonicalUrl },
                        ],
                    })}
                </script>
            </Helmet>

            <ArticleSchema article={article} />

            <div className="bg-[#faf9fc] min-h-screen pb-24">
                <div className="container-custom pt-10 md:pt-14">
                    {/* Breadcrumb */}
                    <nav className="max-w-4xl mx-auto text-sm text-gray-400 mb-6 flex items-center gap-2">
                        <Link to="/" className="hover:text-primary transition-colors">Accueil</Link>
                        <span>/</span>
                        <Link to="/blog/" className="hover:text-primary transition-colors">Blog</Link>
                        {article.tags?.[0] && (
                            <>
                                <span>/</span>
                                <span className="text-gray-500">{article.tags[0]}</span>
                            </>
                        )}
                    </nav>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5 }}
                        className="max-w-4xl mx-auto"
                    >
                        {article.tags?.[0] && (
                            <span className="inline-block bg-primary/10 text-primary text-xs font-semibold tracking-wide uppercase px-3 py-1.5 rounded-full mb-5">
                                {article.tags[0]}
                            </span>
                        )}

                        <h1 className="text-3xl md:text-5xl font-bold text-gray-900 leading-tight tracking-tight mb-6">
                            {article.title}
                        </h1>

                        <div className="flex flex-wrap items-center justify-between gap-4 pb-6">
                            <div className="flex flex-col gap-4">
                                <div className="flex flex-wrap gap-6 text-sm text-gray-500">
                                    <div className="flex items-center">
                                        <User className="h-4 w-4 mr-2 text-primary" />
                                        <span>{article.author}</span>
                                    </div>
                                    <div className="flex items-center">
                                        <Calendar className="h-4 w-4 mr-2 text-primary" />
                                        <span>{new Date(article.date).toLocaleDateString('fr-FR', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric'
                                        })}</span>
                                    </div>
                                    <div className="flex items-center">
                                        <Clock className="h-4 w-4 mr-2 text-primary" />
                                        <span>{article.readTime} de lecture</span>
                                    </div>
                                </div>
                                {article.dateModified && article.dateModified !== article.date && (
                                    <div className="text-xs text-gray-400 italic">
                                        Mis à jour le {new Date(article.dateModified).toLocaleDateString('fr-FR', {
                                            year: 'numeric',
                                            month: 'long',
                                            day: 'numeric'
                                        })}
                                    </div>
                                )}
                            </div>
                            {shareButtons('sm')}
                        </div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 16 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.5, delay: 0.1 }}
                        className="max-w-4xl mx-auto mb-10"
                    >
                        <div className="w-full h-[280px] md:h-[440px] rounded-2xl overflow-hidden shadow-sm">
                            <img
                                src={article.image}
                                alt={article.title}
                                className="w-full h-full object-cover"
                                fetchPriority="high"
                            />
                        </div>
                    </motion.div>

                    <div className="max-w-6xl mx-auto grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_260px] gap-10 items-start">
                        <div>
                            {/* Mobile TOC */}
                            {toc.length > 0 && (
                                <div className="lg:hidden mb-8 bg-white rounded-xl border border-gray-100 shadow-sm">
                                    <button
                                        onClick={() => setMobileTocOpen((v) => !v)}
                                        className="w-full flex items-center justify-between px-5 py-4 text-left"
                                    >
                                        <span className="flex items-center gap-2 font-semibold text-gray-900">
                                            <ListTree size={18} className="text-primary" />
                                            Sommaire
                                        </span>
                                        <ChevronDown
                                            size={18}
                                            className={`text-gray-400 transition-transform ${mobileTocOpen ? 'rotate-180' : ''}`}
                                        />
                                    </button>
                                    {mobileTocOpen && <div className="px-5 pb-5">{renderTocList()}</div>}
                                </div>
                            )}

                            {/* TL;DR / summary callout — for readers and GEO/AI citation */}
                            <div className="bg-white rounded-2xl border border-primary/15 shadow-sm p-6 md:p-7 mb-10 flex gap-4">
                                <Sparkles className="h-5 w-5 text-primary shrink-0 mt-0.5" />
                                <div>
                                    <p className="text-xs font-semibold uppercase tracking-wide text-primary mb-2">L'essentiel</p>
                                    <p className="text-gray-700 leading-relaxed">{article.excerpt}</p>
                                </div>
                            </div>

                            {/* Article Content */}
                            <div className="bg-white rounded-2xl shadow-sm p-6 md:p-10">
                                <article
                                    ref={contentRef}
                                    className="prose prose-lg max-w-none prose-headings:font-bold prose-headings:tracking-tight prose-headings:text-gray-900 prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-strong:text-gray-900 prose-img:rounded-xl"
                                >
                                    <MarkdownRenderer content={article.content} />
                                </article>

                                {/* Tags */}
                                <div className="mt-12 pt-8 border-t border-gray-100">
                                    <div className="flex flex-wrap gap-2">
                                        {article.tags.map((tag) => (
                                            <span key={tag} className="px-3 py-1 bg-gray-100 text-gray-600 rounded-full text-sm">
                                                #{tag}
                                            </span>
                                        ))}
                                    </div>
                                </div>

                                {/* CTA */}
                                <div className="mt-12 bg-gray-900 rounded-2xl p-8 md:p-10 text-center text-white">
                                    <h3 className="text-2xl font-bold mb-4">Envie d'aller plus loin ?</h3>
                                    <p className="mb-6 opacity-90">
                                        Découvrez nos formations pour maîtriser les outils IA et booster votre productivité.
                                    </p>
                                    <Link
                                        to="/formation-ia-entreprise/"
                                        className="inline-block bg-primary hover:bg-primary-light text-white font-bold py-3 px-8 rounded-full transition-colors"
                                    >
                                        Voir nos formations
                                    </Link>
                                </div>
                            </div>

                            {/* Author bio */}
                            <div className="mt-4 bg-white rounded-2xl shadow-sm p-6 md:p-8 flex gap-5 items-start">
                                <div className="w-16 h-16 rounded-full overflow-hidden shrink-0 bg-primary/10">
                                    {authorAvatar ? (
                                        <img src={authorAvatar} alt={article.author} className="w-full h-full object-cover" />
                                    ) : (
                                        <div className="w-full h-full flex items-center justify-center text-primary font-bold text-lg">
                                            {article.author.charAt(0)}
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <p className="text-xs font-semibold uppercase tracking-wide text-primary mb-1">Écrit par</p>
                                    <p className="font-bold text-gray-900 mb-2">{article.author}</p>
                                    <p className="text-sm text-gray-600 leading-relaxed">
                                        {authorBio || "Expert Data & IA chez Upnov IA, accompagnant les PME et ETI dans leur transformation IA."}
                                    </p>
                                </div>
                            </div>

                            {/* FAQ — boosts SEO/GEO citability */}
                            {faq && faq.length > 0 && (
                                <div className="mt-6 bg-white rounded-2xl shadow-sm p-6 md:p-10">
                                    <h2 className="text-2xl font-bold text-gray-900 tracking-tight mb-6">Questions fréquentes</h2>
                                    <div className="divide-y divide-gray-100">
                                        {faq.map((item) => (
                                            <div key={item.question} className="py-4">
                                                <p className="font-semibold text-gray-900 mb-2">{item.question}</p>
                                                <p className="text-sm text-gray-600 leading-relaxed">{item.answer}</p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Formations recommandées — maille interne vers les pages formation */}
                            {recommendedCourses.length > 0 && (
                                <div className="mt-6 bg-white rounded-2xl shadow-sm p-6 md:p-10">
                                    <h2 className="flex items-center gap-2 text-2xl font-bold text-gray-900 tracking-tight mb-6">
                                        <GraduationCap className="h-6 w-6 text-primary" />
                                        Formations recommandées
                                    </h2>
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        {recommendedCourses.map((course) => (
                                            <Link
                                                key={course.id}
                                                to={`/formationIA/${course.slug}/`}
                                                className="group flex flex-col justify-between bg-gray-50 hover:bg-primary/5 rounded-xl p-5 transition-colors"
                                            >
                                                <div>
                                                    <span className="inline-block bg-primary/10 text-primary text-xs font-semibold uppercase tracking-wide py-1 px-3 rounded-full mb-3">
                                                        {course.duration}
                                                    </span>
                                                    <p className="font-semibold text-gray-900 mb-1 leading-snug">{course.title}</p>
                                                    <p className="text-sm text-gray-500 line-clamp-2">{course.description}</p>
                                                </div>
                                                <span className="mt-4 inline-flex items-center text-sm font-medium text-primary">
                                                    Voir la formation
                                                    <ChevronRight className="ml-1 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                                                </span>
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {/* Related Articles */}
                            <div className="mt-6">
                                <RelatedArticles articles={relatedArticles} />
                            </div>

                            <div className="mt-10 text-center">
                                <Link
                                    to="/blog/"
                                    className="inline-flex items-center text-gray-600 hover:text-primary font-medium transition-colors"
                                >
                                    <ArrowLeft className="mr-2 h-4 w-4" />
                                    Retour au blog
                                </Link>
                            </div>
                        </div>

                        {/* Sticky sidebar */}
                        <aside className="hidden lg:block sticky top-24 space-y-6">
                            {toc.length > 0 && (
                                <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                                    <p className="flex items-center gap-2 text-sm font-semibold text-gray-900 mb-3">
                                        <ListTree size={16} className="text-primary" />
                                        Sommaire
                                    </p>
                                    {renderTocList()}
                                </div>
                            )}

                            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
                                <p className="text-sm font-semibold text-gray-900 mb-3">Partager cet article</p>
                                {shareButtons('lg')}
                            </div>

                            <div className="bg-primary/5 rounded-2xl p-5">
                                <p className="text-sm font-semibold text-gray-900 mb-2">Diagnostic IA gratuit</p>
                                <p className="text-sm text-gray-600 mb-4">Évaluez la maturité IA de votre entreprise en quelques minutes.</p>
                                <Link
                                    to="/demarrer-votre-diagnostic-ia-entreprise/"
                                    className="inline-block w-full text-center bg-primary hover:bg-primary-light text-white text-sm font-semibold py-2.5 px-4 rounded-full transition-colors"
                                >
                                    Commencer
                                </Link>
                            </div>
                        </aside>
                    </div>
                </div>
            </div>
        </>
    );
};

export default BlogArticle;
